import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaLinkedin, FaTwitter, FaFacebook, FaCheckCircle, FaHeadset, FaGlobe, FaRobot } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const ContactUsPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
    });

    const [userCaptcha, setUserCaptcha] = useState('');
    const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
    const [captchaError, setCaptchaError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    // Generate Captcha on mount
    React.useEffect(() => {
        generateCaptcha();
    }, []);

    const generateCaptcha = () => {
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setCaptcha({ num1, num2 });
        setUserCaptcha('');
        setCaptchaError('');
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate Captcha
        if (!userCaptcha) {
            setCaptchaError('Please complete the captcha verification.');
            return;
        }

        if (parseInt(userCaptcha) !== captcha.num1 + captcha.num2) {
            const num1 = Math.floor(Math.random() * 10) + 1;
            const num2 = Math.floor(Math.random() * 10) + 1;
            setCaptcha({ num1, num2 });
            setUserCaptcha('');
            setCaptchaError('Invalid captcha please try again');
            return;
        }

        // Handle form submission here
        console.log('Form submitted:', formData);
        setSubmitted(true);
        setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            service: '',
            message: ''
        });
        generateCaptcha(); // Regenerate for next time
        setTimeout(() => setSubmitted(false), 5000);
    };

    const services = [
        'Infrastructure Solutions',
        'Network & Cabling',
        'Telecom Solutions',
        'Security Systems',
        'National Rollouts',
        'IT Support',
        'IT Deployment',
        'Break & Fix',
        'Other'
    ];

    const contactInfo = [
        {
            icon: <FaPhone size={24} />,
            title: 'Phone',
            primary: '+1 201-409-9845',
            secondary: 'Mon-Fri 9AM-6PM EST',
            gradient: 'from-blue-500 to-cyan-500',
            link: 'tel:+12014099845'
        },
        {
            icon: <FaEnvelope size={24} />,
            title: 'Email',
            primary: 'info@nextgenltdco.com',
            secondary: 'We reply within 24 hours',
            gradient: 'from-purple-500 to-pink-500',
            link: 'mailto:info@nextgenltdco.com'
        },
        {
            icon: <FaMapMarkerAlt size={24} />,
            title: 'Headquarters',
            primary: '8011 34th Ave. So, Suite - C30',
            secondary: 'Bloomington, MN - 55425',
            gradient: 'from-orange-500 to-red-500',
            link: 'https://maps.google.com/?q=8011+34th+Ave+So+Suite+C30+Bloomington+MN+55425'
        },
        {
            icon: <FaClock size={24} />,
            title: 'Business Hours',
            primary: 'Mon - Fri: 9AM - 6PM',
            secondary: '24/7 Support Available',
            gradient: 'from-green-500 to-teal-500',
            link: null
        }
    ];

    const reasons = [
        { icon: <FaHeadset size={20} />, text: 'Expert consultation' },
        { icon: <FaGlobe size={20} />, text: 'Nationwide coverage' },
        { icon: <FaCheckCircle size={20} />, text: 'Reliable Service' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            <main className="overflow-x-hidden">
                {/* Hero Section */}
                <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 py-20 overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                        }}></div>
                    </div>

                    {/* Floating shapes */}
                    <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500 rounded-full filter blur-3xl opacity-10"></div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                            <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                                <FaHome size={14} />
                                <span>Home</span>
                            </Link>
                            <FaChevronRight size={10} className="text-gray-500" />
                            <span className="text-cyan-400">Contact Us</span>
                        </nav>

                        <div className="text-center max-w-3xl mx-auto">
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Let's <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Connect</span>
                            </h1>
                            <p className="text-xl text-gray-300 leading-relaxed">
                                Ready to transform your IT infrastructure? Our team of experts is here to help you achieve your technology goals.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Contact Info Cards */}
                <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white relative -mt-8 z-20">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {contactInfo.map((info, index) => (
                                <div
                                    key={index}
                                    className="group bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 relative overflow-hidden"
                                >
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-50 to-blue-50 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500"></div>
                                    <div className="relative z-10">
                                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                            {info.icon}
                                        </div>
                                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{info.title}</h3>
                                        {info.link ? (
                                            <a href={info.link} className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors block mb-1">
                                                {info.primary}
                                            </a>
                                        ) : (
                                            <p className="text-lg font-bold text-gray-900 mb-1">{info.primary}</p>
                                        )}
                                        <p className="text-sm text-gray-500">{info.secondary}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Main Contact Section */}
                <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid lg:grid-cols-5 gap-12">
                            {/* Left Side - Info */}
                            <div className="lg:col-span-2 space-y-8">
                                <div>
                                    <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Get in Touch</span>
                                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">We'd Love to Hear From You</h2>
                                    <p className="text-gray-600 leading-relaxed">
                                        Whether you have a question about our services, need a quote, or want to discuss a project, we're here to help.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {reasons.map((reason, index) => (
                                        <div key={index} className="flex items-center gap-3 text-gray-700">
                                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white">
                                                {reason.icon}
                                            </div>
                                            <span className="font-medium">{reason.text}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Social Links */}
                                <div className="pt-8 border-t border-gray-200">
                                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Follow Us</p>
                                    <div className="flex gap-4">
                                        <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-blue-600 hover:shadow-lg transition-all duration-300">
                                            <FaLinkedin size={20} />
                                        </a>
                                        <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-blue-400 hover:shadow-lg transition-all duration-300">
                                            <FaTwitter size={20} />
                                        </a>
                                        <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-blue-800 hover:shadow-lg transition-all duration-300">
                                            <FaFacebook size={20} />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Form */}
                            <div className="lg:col-span-3">
                                <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 relative overflow-hidden">
                                    {/* Decorative gradient */}
                                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500"></div>

                                    {submitted ? (
                                        <div className="text-center py-16">
                                            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                                                <FaCheckCircle size={40} className="text-green-500" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                                            <p className="text-gray-600">We'll get back to you within 24 hours.</p>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 outline-none"
                                                        placeholder="John Doe"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 outline-none"
                                                        placeholder="john@company.com"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 outline-none"
                                                        placeholder="+1 (555) 000-0000"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
                                                    <input
                                                        type="text"
                                                        name="company"
                                                        value={formData.company}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 outline-none"
                                                        placeholder="Your Company"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Service of Interest</label>
                                                <select
                                                    name="service"
                                                    value={formData.service}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 outline-none bg-white"
                                                >
                                                    <option value="">Select a service</option>
                                                    {services.map((service, index) => (
                                                        <option key={index} value={service}>{service}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                                                <textarea
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                    rows={5}
                                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 outline-none resize-none"
                                                    placeholder="Tell us about your project or requirements..."
                                                ></textarea>
                                            </div>

                                            {/* Styled AI Captcha */}
                                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 relative overflow-hidden group/captcha hover:border-blue-300 transition-colors duration-300">
                                                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-full blur-xl -mr-10 -mt-10"></div>

                                                <div className="flex items-start gap-4 relative z-10">
                                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white shrink-0 shadow-lg shadow-blue-500/20">
                                                        <FaRobot size={20} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <label className="block text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                                                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-600">
                                                                Captcha Verification
                                                            </span>
                                                        </label>
                                                        <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                                                            <div className="px-3 py-2 bg-white rounded-lg border border-gray-200 font-mono font-bold text-blue-600 select-none shadow-sm whitespace-nowrap shrink-0">
                                                                {captcha.num1} + {captcha.num2} = ?
                                                            </div>
                                                            <input
                                                                type="number"
                                                                value={userCaptcha}
                                                                onChange={(e) => {
                                                                    setUserCaptcha(e.target.value);
                                                                    if (captchaError) setCaptchaError('');
                                                                }}
                                                                className={`flex-1 min-w-[120px] px-4 py-2 rounded-lg border focus:ring-2 transition-all duration-300 outline-none bg-white font-medium text-center ${captchaError
                                                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                                                                    : 'border-gray-200 focus:border-cyan-500 focus:ring-cyan-100'
                                                                    }`}
                                                                placeholder="Answer"
                                                            />
                                                        </div>
                                                        {captchaError && (
                                                            <div className="mt-3 flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-100 text-xs font-semibold animate-pulse">
                                                                <div className="w-1.5 h-1.5 rounded-full bg-red-600"></div>
                                                                {captchaError}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 flex items-center justify-center gap-2 group"
                                            >
                                                <FaPaperPlane size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                                                Send Message
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Prefer to Talk?</h2>
                        <p className="text-xl text-blue-100 mb-8">
                            Give us a call and speak directly with our team.
                        </p>
                        <a
                            href="tel:+12014099845"
                            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-blue-600 rounded-2xl font-bold text-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                        >
                            <FaPhone size={20} />
                            +1 201-409-9845
                        </a>
                    </div>
                </section>

                <Footer />
            </main>
        </div>
    );
};

export default ContactUsPage;
